import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Sun, Moon, Check, LogIn, LayoutDashboard, LogOut, User as UserIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const BrandLogo = ({ brand }) => (
  <div className="flex items-center gap-3">
    <img
      src={brand.logoUrl}
      alt={brand.logoAlt}
      className="h-9 md:h-10 w-auto"
      draggable={false}
    />
  </div>
);

// Neutral wordmark used on the PTI page (brand logo removed on request).
const NeutralWordmark = () => (
  <div className="flex items-center gap-3 select-none">
    <span
      className="text-[13px] tracking-[0.32em] font-semibold uppercase bg-clip-text text-transparent"
      style={{ backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)' }}
    >
      Pricing Studio
    </span>
  </div>
);

const CurrencyBadge = () => {
  const { code, currency } = useCurrency();
  return (
    <div
      className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[12px] font-semibold text-white/80 flex items-center gap-2"
      title={`Prices shown in ${code} (${currency.locale})`}
      data-testid="currency-badge"
    >
      <span className="text-cyan-300">{currency.symbol}</span>
      <span className="tracking-widest">·</span>
      <span>{code}</span>
    </div>
  );
};

const UserMenu = () => {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  if (!user) {
    return (
      <Link
        to="/login"
        className="h-9 px-3 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-[12px] tracking-wider uppercase flex items-center gap-2 transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        Sign in
      </Link>
    );
  }

  const initial = (user.full_name || user.email || 'U').slice(0, 1).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="h-9 pl-1.5 pr-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[12px] font-semibold text-white/85 flex items-center gap-2 transition-colors"
      >
        <span className="h-6 w-6 rounded-md bg-gradient-to-br from-cyan-400 to-purple-500 text-black flex items-center justify-center text-[11px] font-bold">
          {initial}
        </span>
        <span className="hidden sm:inline">{user.full_name?.split(' ')[0] || 'You'}</span>
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg bg-[#12121a] border border-white/10 shadow-2xl overflow-hidden z-50">
          <div className="px-3 py-3 border-b border-white/5">
            <div className="text-[13px] text-white truncate">{user.full_name}</div>
            <div className="text-[11px] text-white/45 truncate">{user.email}</div>
            <div className="mt-1.5 text-[10px] tracking-[0.16em] uppercase font-semibold text-emerald-300">
              {user.role?.replace('_', ' ')}
            </div>
          </div>
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              data-testid="admin-panel-link"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-white/80 hover:bg-white/5 hover:text-white transition-colors"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              Admin panel
            </Link>
          )}
          <button
            onClick={() => { logout(); setOpen(false); navigate('/'); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [dark, setDark] = useState(true);
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { brand } = useBrand();
  const isPti = location.pathname.startsWith('/pti') || location.pathname.startsWith('/itanta');
  const isCore = location.pathname.startsWith('/core');
  const isStreamline = location.pathname.startsWith('/streamline');
  // Only enforce restrictions for logged-in non-admin users.
  const canPTI = !user || isAdmin || (user.allowed_products || ['pti', 'core', 'streamline']).includes('pti');
  const canCore = !user || isAdmin || (user.allowed_products || ['pti', 'core', 'streamline']).includes('core');
  const canStreamline = !user || isAdmin || (user.allowed_products || ['pti', 'core', 'streamline']).includes('streamline');

  // Brand shown in the top-left:
  //  - /streamline* -> always Streamline360 logo (that route is Streamline-specific)
  //  - /pti*|/itanta -> Neutral wordmark (Itanta logo hidden on PTI page per original requirement)
  //  - everywhere else (landing, /core, etc.) -> current global brand logo
  let BrandEl;
  if (isStreamline) {
    BrandEl = () => (
      <img
        src="https://customer-assets-lxgj4vgw.emergentagent.net/job_itanta-pricing/artifacts/4wfxb5mg_streamline_logo_variant_e.png"
        alt="Streamline360"
        className="h-9 md:h-10 w-auto"
        draggable={false}
      />
    );
  } else if (isPti) {
    BrandEl = NeutralWordmark;
  } else {
    BrandEl = () => <BrandLogo brand={brand} />;
  }
  const brandLinkTo = isStreamline ? '/streamline' : '/';

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-black/60 border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to={brandLinkTo} className="flex items-center">
          <div className="rounded-md px-3 py-1.5" style={{ boxShadow: '0 0 0 1px rgba(255,255,255,0.04)' }}>
            <BrandEl />
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-[11px] tracking-[0.18em] font-semibold uppercase">
          {canStreamline && (
          <Link to="/streamline" className={`transition-colors flex items-center gap-2 ${isStreamline ? 'text-cyan-300' : 'text-white/60 hover:text-white/90'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${isStreamline ? 'bg-cyan-300' : 'bg-teal-400/60'}`} />
            Streamline
          </Link>
          )}
          {canCore && (
          <Link to="/core" className={`transition-colors flex items-center gap-2 ${isCore ? 'text-cyan-300' : 'text-white/60 hover:text-white/90'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${isCore ? 'bg-cyan-300' : 'bg-purple-400/60'}`} />
            Core
          </Link>
          )}
          {isAdmin && canPTI && (
          <Link to="/pti" className={`transition-colors flex items-center gap-2 ${isPti ? 'text-cyan-300' : 'text-white/40 hover:text-white/80'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${isPti ? 'bg-cyan-300' : 'bg-white/30'}`} />
            PTI (Admin)
          </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <CurrencyBadge />
          <UserMenu />
          <button
            onClick={() => setDark(!dark)}
            className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
            aria-label="toggle theme"
          >
            {dark ? (
              <Moon className="w-4 h-4 text-white/70" />
            ) : (
              <Sun className="w-4 h-4 text-white/70" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
